import { useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { RefreshCw, MessageCircle } from 'lucide-react';

export function PaymentFailPage() {
  const [params] = useSearchParams();
  const paymentId = params.get('payment_id');

  useEffect(() => {
    if (window.parent !== window) {
      window.parent.postMessage({
        type: 'payment-result',
        status: 'fail',
        paymentId,
      }, '*');
    }
  }, [paymentId]);

  const failureMessage = params.get('failure_message') ||
    'Ödeme tamamlanamadı. Genelde küçük bir banka pürüzü olur.';

  return (
    <div
      className="min-h-screen relative overflow-hidden flex items-center justify-center px-6 py-12"
      style={{
        background: `
          radial-gradient(circle at 20% 80%, rgba(225,29,72,0.20) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(251,146,60,0.12) 0%, transparent 50%),
          radial-gradient(circle at 50% 50%, rgba(220,38,38,0.06) 0%, transparent 60%),
          #0A0F1E
        `,
      }}
    >
      <style>{`
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
        }
        .pulse-dot { animation: pulse-dot 1.5s ease-in-out infinite; }

        .gradient-title-fail {
          background: linear-gradient(135deg, #FB7185 0%, #F43F5E 50%, #F97316 100%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
      `}</style>

      <div className="relative z-10 w-full max-w-xl">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 mb-6"
          style={{
            background: 'rgba(225,29,72,0.15)',
            border: '1px solid rgba(225,29,72,0.3)',
            padding: '6px 14px',
            borderRadius: '100px',
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '11px',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: '#FB7185',
          }}
        >
          <span
            className="pulse-dot"
            style={{
              width: '6px',
              height: '6px',
              background: '#FB7185',
              borderRadius: '50%',
              boxShadow: '0 0 12px #FB7185',
            }}
          />
          İşlem reddedildi
        </div>

        {/* Title */}
        <h1
          className="gradient-title-fail"
          style={{
            fontFamily: "'Bowlby One', cursive",
            fontSize: 'clamp(64px, 10vw, 96px)',
            lineHeight: 0.85,
            letterSpacing: '-0.04em',
            marginBottom: '8px',
            textTransform: 'uppercase',
          }}
        >
          HOPS!
        </h1>

        {/* Subtitle */}
        <h2
          style={{
            fontFamily: "'Outfit', sans-serif",
            fontWeight: 700,
            fontSize: 'clamp(24px, 3vw, 32px)',
            lineHeight: 1.1,
            marginBottom: '16px',
            color: 'white',
          }}
        >
          Ödeme tamamlanamadı.
        </h2>

        {/* Body */}
        <p
          style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: '16px',
            lineHeight: 1.55,
            color: '#94A3B8',
            marginBottom: '32px',
            maxWidth: '460px',
          }}
        >
          {failureMessage} <strong style={{ color: 'white' }}>Hiçbir şey çekilmedi</strong>, içiniz rahat olsun.
          Limit, 3D şifre ya da geçici bir bağlantı sorunu olabilir. Bir kez daha deneyelim.
        </p>

        {/* Stat kartları */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          <StatCard value="₺0" label="Çekilen" valueColor="#FB7185" />
          <StatCard value="~30sn" label="Tekrar dene" valueColor="#94A3B8" />
          <StatCard value="7/24" label="Destek" valueColor="#60A5FA" />
        </div>

        {/* Actions */}
        <div className="flex gap-3 flex-wrap">
          <Link to="/billing" className="flex-1 min-w-[200px]">
            <button
              className="w-full"
              style={{
                padding: '16px 28px',
                borderRadius: '12px',
                border: 'none',
                fontFamily: "'Outfit', sans-serif",
                fontWeight: 700,
                fontSize: '15px',
                cursor: 'pointer',
                background: 'linear-gradient(135deg, #DC2626 0%, #F97316 100%)',
                color: 'white',
                boxShadow: '0 4px 24px rgba(220,38,38,0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(220,38,38,0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 24px rgba(220,38,38,0.4)';
              }}
            >
              <RefreshCw size={16} />
              Yeniden dene
            </button>
          </Link>

          <a href="mailto:destek@fieldpin.io">
            <button
              style={{
                padding: '16px 28px',
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.12)',
                fontFamily: "'Outfit', sans-serif",
                fontWeight: 700,
                fontSize: '15px',
                cursor: 'pointer',
                background: 'rgba(255,255,255,0.06)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background 0.2s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
            >
              <MessageCircle size={16} />
              Destek
            </button>
          </a>
        </div>

        {/* Footer */}
        <div
          style={{
            marginTop: '24px',
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '11px',
            color: '#64748B',
            letterSpacing: '0.05em',
          }}
        >
          {paymentId && <>İşlem · {paymentId.slice(0, 8)} · </>}
          <a
            href="mailto:destek@fieldpin.io"
            style={{ color: '#FB7185', textDecoration: 'none' }}
          >
            destek@fieldpin.io
          </a>
        </div>
      </div>
    </div>
  );
}

function StatCard({ value, label, valueColor = '#FBBF24' }) {
  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '12px',
        padding: '16px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          fontFamily: "'Bowlby One', cursive",
          fontSize: '22px',
          color: valueColor,
          marginBottom: '4px',
          letterSpacing: '-0.02em',
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: '10px',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: '#94A3B8',
        }}
      >
        {label}
      </div>
    </div>
  );
}
